import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Stack
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLevels } from '../hooks'
import { ReactNode } from 'react'
import LevelCreateButton from './LevelCreateButton'
import { Project } from '../types'

interface LevelSelectProps extends Omit<
  SelectProps<number | null>,
  'value' | 'onChange' | 'children'
> {
  project?: Project
  value: number | null
  error?: boolean
  helperText?: ReactNode
  withLabel?: boolean
  withAddOption?: boolean
  withClearSelection?: boolean
  onChange: (event: SelectChangeEvent<number | null>, child: ReactNode) => void
}

export default function LevelSelect({
  project,
  label,
  value,
  error,
  helperText,
  disabled,
  withLabel = true,
  withAddOption = false,
  withClearSelection = false,
  onChange,
  ...props
}: LevelSelectProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The levels.
   */
  const levels = useLevels()

  const sortedLevels = [...levels].sort((a, b) => a.name.localeCompare(b.name))

  const translatedLabel = label ?? t('level')

  const handleChange = (event: SelectChangeEvent<number | null>, child: ReactNode) => {
    const value = event.target.value

    if (value === undefined) {
      event.target.value = null
    }

    onChange(event, child)
  }

  const getLevelName = (id: number) => {
    const level = levels.find((level) => level.id === id)

    return level?.name ?? id?.toString()
  }

  return (
    <FormControl fullWidth>
      {withLabel && (
        <InputLabel shrink={true} sx={{ mb: 1 }}>
          {translatedLabel}
        </InputLabel>
      )}
      <Select
        label={translatedLabel}
        value={value ?? ''}
        MenuProps={{
          keepMounted: withAddOption
        }}
        displayEmpty={true}
        disabled={disabled}
        renderValue={(value) => {
          return value === undefined || value == null ? t('areaLevel') : getLevelName(value)
        }}
        fullWidth
        {...props}
        onChange={handleChange}
      >
        <MenuItem key="area" selected={value === null || value === undefined} value={undefined}>
          {t('areaLevel')}
        </MenuItem>

        {levels.length && <Divider />}

        {sortedLevels.map((level) => (
          <MenuItem key={level.id} value={level.id}>
            {level.name}
          </MenuItem>
        ))}

        {!disabled && withAddOption && <Divider />}

        {!disabled && withAddOption && (
          <Box sx={{ px: 4, pt: 1 }} onClick={(e) => e.stopPropagation()}>
            <LevelCreateButton variant="text" color="primary" fullWidth project={project} />
          </Box>
        )}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}

      {value !== null && withClearSelection && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }}>
          <Button
            type="button"
            variant="text"
            onClick={() => {
              onChange?.(
                {
                  target: {
                    name: props.name,
                    value: null
                  }
                } as SelectChangeEvent<number | null>,
                {} as ReactNode
              )
            }}
          >
            {t('clearSelection')}
          </Button>
        </Stack>
      )}
    </FormControl>
  )
}
